import { Box, Container, Typography, Paper, Divider } from "@mui/material";

export function TermsOfService() {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: 6 }}>
      <Container maxWidth="md">
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 6 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: May 15, 2026
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Section title="1. About BuildBridge">
            BuildBridge ("we", "us", or "our") provides a software platform that
            helps contractors and their clients communicate, share project plans,
            post progress updates, and manage documents. BuildBridge is a
            technology platform only. We are not a licensed general contractor,
            construction manager, or party to any agreement between a contractor
            and their client.
          </Section>

          <Section title="2. Acceptance of Terms">
            By creating an account, you agree to these Terms of Service and our
            Privacy Policy. If you do not agree, do not use BuildBridge. We may
            update these terms from time to time; continued use of the platform
            after notice of changes constitutes acceptance.
          </Section>

          <Section title="3. Accounts and Roles">
            BuildBridge has two account types: Contractors and Clients.
            Contractors pay a monthly subscription to access the full platform.
            Clients are invited by contractors and use the platform for free.
            You are responsible for maintaining the confidentiality of your
            credentials and for all activity under your account.
          </Section>

          <Section title="4. Subscriptions and Billing">
            Contractor subscriptions are billed monthly via Stripe. Your
            subscription renews automatically at the end of each billing period
            unless you cancel. Cancellation takes effect at the end of the
            current billing period — you retain access until then. We do not
            offer prorated refunds for partial billing periods.
          </Section>

          <Section title="5. Plan Approvals">
            The "Approve Plan" feature allows clients to acknowledge a project
            plan submitted by their contractor within the BuildBridge platform.
            This approval is an in-app acknowledgment only and does not
            constitute a legally binding contract between the contractor and
            client. Parties should execute a separate signed contract for any
            legally enforceable agreement.
          </Section>

          <Section title="6. Contractor–Client Relationship">
            BuildBridge is not a party to any agreement, dispute, or financial
            transaction between contractors and clients. Any disputes regarding
            project scope, payment, or quality of work are solely between the
            contractor and client. BuildBridge has no obligation to mediate,
            arbitrate, or resolve such disputes.
          </Section>

          <Section title="7. User Content and Documents">
            You retain ownership of content you upload to BuildBridge (messages,
            documents, photos). By uploading content, you grant BuildBridge a
            limited license to store and display it to authorized users of your
            project. We are not responsible for the accuracy, legality, or
            completeness of any documents you upload. You are responsible for
            maintaining your own copies of important files.
          </Section>

          <Section title="8. Prohibited Use">
            You may not use BuildBridge to: (a) violate any applicable law;
            (b) submit false or fraudulent project information; (c) harass,
            threaten, or harm other users; (d) attempt to gain unauthorized
            access to any part of the platform or another user's data.
          </Section>

          <Section title="9. Limitation of Liability">
            To the fullest extent permitted by law, BuildBridge is not liable
            for any indirect, incidental, special, or consequential damages
            arising out of your use of the platform, including but not limited
            to lost profits, data loss, or project disputes. Our total liability
            to you will not exceed the amount you paid us in the three months
            preceding the claim.
          </Section>

          <Section title="10. Termination">
            We may suspend or terminate your account if you violate these terms.
            You may delete your account at any time from Settings. Upon
            termination, your data will be handled in accordance with our
            Privacy Policy.
          </Section>

          <Section title="11. Governing Law">
            These terms are governed by the laws of the state in which
            BuildBridge is incorporated, without regard to conflict of law
            principles.
          </Section>

          <Section title="12. Contact">
            Questions about these terms? Contact us at{" "}
            <strong>legal@buildbridge.app</strong>.
          </Section>
        </Paper>
      </Container>
    </Box>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
        {children}
      </Typography>
    </Box>
  );
}
